'use client';

import * as SelectPrimitive from '@radix-ui/react-select';
import { ArrowDownIcon, ArrowUpIcon, CloseIcon, LoadingCircleIcon } from '@repo/icons';
import { cn } from '@repo/utils/cn';
import { Check } from 'lucide-react';
import * as React from 'react';
import { labelVariants } from '../label';

interface SelectTriggerProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  loading?: boolean;
  allowClear?: boolean;
  onClear?: () => void;
}

export type SelectFieldDataItem = {
  label: string;
  value: string;
};

type SelectFieldProps = {
  label?: string;
  data: SelectFieldDataItem[];
  onChange: (value: string) => void;
  selectedValue: string;
  getSelectedLabel?: (selectedValue: string) => string;
  disabled?: boolean;
  className?: string;
};

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Value>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Value
    ref={ref}
    className={cn('vt-input-default-text-normal', className)}
    {...props}
  />
));

SelectValue.displayName = SelectPrimitive.Value.displayName;

const SelectTrigger = React.forwardRef<React.ComponentRef<typeof SelectPrimitive.Trigger>, SelectTriggerProps>(
  ({ className, loading = false, children, allowClear = false, value, onClear, ...props }, ref) => {
    const handleClearClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onClear) {
        onClear();
      }
    };

    return (
      <SelectPrimitive.Trigger
        ref={ref}
        data-testid="select-trigger"
        className={cn(
          'vt-input-default-bg-normal vt-input-default-text-normal vt-input-default-stroke-normal vt-input-text-size-normal focus-visible:vt-input-default-bg-focus focus-visible:vt-input-default-stroke-focus focus-visible:vt-input-default-text-focus focus-visible:vt-atoms-input-focus-box-shadow disabled:vt-input-default-bg-disabled disabled:vt-input-default-stroke-disabled disabled:vt-input-default-text-disabled flex h-10 w-full min-w-0 items-center justify-between rounded-[4px] border p-1.5 text-left focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed rtl:text-right [&>span]:line-clamp-2',
          {
            'vt-input-default-text-focus': !!value,
          },
          className
        )}
        {...props}
      >
        <>
          {children}
          <SelectPrimitive.Icon asChild>
            <span>
              {loading ? (
                <LoadingCircleIcon className="pr-1 opacity-50 rtl:pl-1" />
              ) : allowClear && value && onClear ? (
                <CloseIcon
                  className="h-3 w-3 cursor-pointer pr-1 opacity-50 rtl:pl-1"
                  onPointerDown={handleClearClick}
                />
              ) : (
                <ArrowDownIcon className="pl-1 opacity-50 rtl:pr-1" />
              )}
            </span>
          </SelectPrimitive.Icon>
        </>
      </SelectPrimitive.Trigger>
    );
  }
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ArrowUpIcon className="pl-3 rtl:pr-3" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ArrowUpIcon className="pl-3 rtl:pr-3" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      // ref={(instance) => {
      //   if (!instance) return;
      //   if (typeof ref === 'function') {
      //     ref(instance);
      //   } else if (ref) {
      //     ref.current = instance;
      //   }
      //   instance.ontouchstart = (e) => e.preventDefault();
      // }}
      className={cn(
        'vt-input-default-bg-focus vt-input-default-text-focus data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 vt-text-size-small sm:vt-text-size-small-sm relative z-[150] max-h-96 min-w-[8rem] overflow-hidden rounded-[4px] shadow-md',
        className,
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1'
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('vt-text-size-small sm:vt-text-size-small-sm py-1.5 pl-8 pr-2', className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & { showCheck?: boolean }
>(({ className, children, showCheck = false, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'vt-input-text-size-normal focus:vt-input-default-bg-focus data-[disabled]:vt-input-default-bg-disabled relative flex w-full cursor-pointer select-none items-center p-1.5 outline-none data-[disabled]:pointer-events-none',
      className
    )}
    {...props}
  >
    {showCheck ? (
      <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
    ) : null}

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

const SelectField = ({
  label,
  data = [],
  onChange,
  selectedValue = '',
  getSelectedLabel,
  disabled,
  className,
}: SelectFieldProps) => {
  const { label: selectedLabel } = data.find((option) => option.value === selectedValue) || {};
  const builtLabel = getSelectedLabel && getSelectedLabel(selectedLabel || '');

  return (
    <div className={cn('w-full space-y-1', className)}>
      {label && <span className={cn(labelVariants())}>{label}</span>}
      <Select
        value={selectedValue}
        disabled={disabled}
        onValueChange={(value) => onChange(value)}
      >
        <SelectTrigger
          className={cn({
            'vt-input-focus-text': selectedValue !== 'NO_OPTION',
          })}
        >
          {builtLabel ? <SelectValue>{builtLabel}</SelectValue> : <SelectValue placeholder={label} />}
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            key="NO_OPTION"
            value="NO_OPTION"
            className="hidden"
          >
            {label}
          </SelectItem>
          {data.map(({ label, value }) => (
            <SelectItem
              key={`${label}-${value}`}
              value={value}
            >
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  SelectField,
};
